'use client';

import { useRef, useState, useEffect, type ImgHTMLAttributes } from 'react';

/**
 * Derive the AVIF / WebP sibling URL from an original image URL by swapping
 * the final extension. For example:
 *
 *   /media/portfolio/hero/avatar-123.jpg  ->  /media/portfolio/hero/avatar-123.avif
 *
 * If the URL has no extension we recognise (e.g. an external URL, a `data:`
 * URL, or an SVG), we return `null` so the caller can skip the corresponding
 * <source> element.
 */
const CONVERTIBLE_EXT_RE = /\.(jpe?g|png|webp|tiff?|avif)(\?.*)?$/i;

function swapExt(url: string, newExt: 'avif' | 'webp'): string | null {
  if (!url) return null;
  // Don't try to rewrite data: URLs, blob:, or fragments
  if (/^(data:|blob:)/i.test(url)) return null;
  if (!CONVERTIBLE_EXT_RE.test(url)) return null;
  return url.replace(CONVERTIBLE_EXT_RE, `.${newExt}$2`);
}

export interface OptimizedImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** The "original" image URL — typically JPG/PNG. AVIF & WebP siblings are
   *  derived from this by extension swap. */
  src: string;
  /** Optional explicit AVIF URL — overrides the derived one. */
  avifSrc?: string;
  /** Optional explicit WebP URL — overrides the derived one. */
  webpSrc?: string;
  /** Forwarded to the wrapping <picture> element. */
  pictureClassName?: string;
  /** Forwarded to the wrapping <picture> element. */
  pictureStyle?: React.CSSProperties;
}

/**
 * Drop-in replacement for `<img>` that serves AVIF / WebP variants when the
 * browser supports them, falling back to the original on older browsers or
 * when a variant isn't on disk (the browser simply skips to the next
 * `<source>` or the `<img>`).
 *
 * While the image is loading, a neutral shimmer skeleton is painted into
 * the <picture> wrapper. This replaces the previous pink / purple gradient
 * fallback that leaked through from surrounding ancestor styles.
 */
export default function OptimizedImage({
  src,
  avifSrc,
  webpSrc,
  pictureClassName,
  pictureStyle,
  alt,
  onLoad,
  style: imgStyle,
  ...rest
}: OptimizedImageProps) {
  const avif = avifSrc ?? swapExt(src, 'avif');
  const webp = webpSrc ?? swapExt(src, 'webp');

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Handle images that are already in the browser cache — their `load`
  // event fires before React attaches the listener, so check `complete`
  // on mount.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const wrapperStyle: React.CSSProperties = {
    // Only paint the shimmer until the image arrives, then fade to nothing
    // so the <picture>'s own background doesn't peek around transparent
    // PNGs once loaded.
    ...(loaded
      ? { background: 'transparent' }
      : {
          background:
            'linear-gradient(110deg, #eef2ff 8%, #f3f4f6 18%, #eef2ff 33%)',
          backgroundSize: '200% 100%',
          animation: 'optimized-image-shimmer 1.6s ease-in-out infinite',
        }),
    ...pictureStyle,
  };

  return (
    <picture className={pictureClassName} style={wrapperStyle}>
      {avif && <source srcSet={avif} type="image/avif" />}
      {webp && <source srcSet={webp} type="image/webp" />}
      {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
      <img
        ref={imgRef}
        src={src}
        alt={alt ?? ''}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        style={{
          ...imgStyle,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-out',
        }}
        {...rest}
      />
    </picture>
  );
}
